import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { useBusinessUnit } from "@/hooks/useBusinessUnit";
import { applyBusinessUnitDefault } from "@/shared/language";

/**
 * Starts a signed-in reader in their business unit's language (PRD H7).
 *
 * Mounted once, in the root route, because it is about the whole app rather
 * than any page: the unit's default has to be applied wherever the reader first
 * lands. It runs after auth resolves — the BU is not known any earlier — so a
 * reader with no choice of their own may see the first paint in one language
 * and the rest in their business unit's. Blocking the app on a Firestore read to avoid
 * that flash would be the worse trade.
 *
 * **The signed-in user's own unit, never the impersonated one.** Language is a
 * property of whoever is reading the screen, and during impersonation that is
 * still the admin. Following the target's unit would switch an admin's entire
 * interface to German because the colleague they are looking at works at a
 * German business unit — the one place where the acting identity is the wrong answer.
 *
 * Does nothing at all for somebody who has used the EN/DE switch: their choice
 * outranks the unit's default permanently (see `shared/language`).
 *
 * **It is a second `onSnapshot` on a document `UserDropdown` already watches,
 * and that costs no second subscription.** The Firestore SDK keys query views
 * by query and reuses the existing target rather than allocating one
 * (`syncEngineListen`), so two listeners on the same doc ref share one listen
 * stream and the second is a local fan-out. Reading the field once with
 * `getDoc` instead would be the cheaper-looking option and the wrong one: a
 * unit correcting its default would then reach nobody until they reloaded,
 * which is the one thing this setting exists to be able to fix.
 */
export function useBusinessUnitLanguage(): void {
  const { realBusinessUnitId } = useAuth();
  const { businessUnit } = useBusinessUnit(realBusinessUnitId);
  const { i18n } = useTranslation();
  const defaultLanguage = businessUnit?.defaultLanguage;

  useEffect(() => {
    if (!defaultLanguage) return;
    applyBusinessUnitDefault(i18n, defaultLanguage);
    // Keyed on the value rather than the document: `useBusinessUnit` streams,
    // so a BU object identity changes on every unrelated edit to the unit, and
    // re-running then would be pointless work on every settings save anyone in
    // the business unit makes.
  }, [defaultLanguage, i18n]);
}
